import { createSlice } from '@reduxjs/toolkit'
import {
  fetchNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  sendDirectNotification,
  sendBulkNotifications,
  sendGdprPrivacyPolicyNotification,
} from '../actions/notificationActions.js'

const initialState = {
  notifications: [],
  singleNotification: null,
  loading: false,
  error: null,
  unreadCount: 0,
  sendLoading: false,
  sendError: null,
  sendSuccess: false,
}

const notificationsSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    resetNotificationState: state => {
      state.notifications = []
      state.loading = false
      state.error = null
    },
    readNotificationsRequest: state => {
      state.loading = true
      state.error = null
    },
    readNotificationsSuccess: (state, action) => {
      state.loading = false
      state.singleNotification = action.payload
      state.error = null

      // Update the notification in the list if it exists
      const index = state.notifications.findIndex(notification => notification._id === action.payload._id)
      if (index !== -1) {
        state.notifications[index] = action.payload
      }

      // Update unread count
      state.unreadCount = state.notifications.filter(n => !n.isRead).length
    },
    readNotificationsFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    clearSendStatus: state => {
      state.sendLoading = false
      state.sendError = null
      state.sendSuccess = false
    },
    clearError: state => {
      state.error = null
      state.sendError = null
    },
  },
  extraReducers: builder => {
    // Fetch notifications
    builder
      .addCase(fetchNotifications.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false
        state.notifications = action.payload
        state.unreadCount = action.payload.filter(notification => !notification.isRead).length
        state.error = null
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Mark as read
    builder
      .addCase(markAsRead.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(markAsRead.fulfilled, (state, action) => {
        state.loading = false
        state.singleNotification = action.payload

        // Update the notification in the list
        const index = state.notifications.findIndex(notification => notification.id === action.payload.id)
        if (index !== -1) {
          state.notifications[index] = action.payload
        }

        // Update unread count
        state.unreadCount = state.notifications.filter(n => !n.isRead).length
        state.error = null
      })
      .addCase(markAsRead.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Mark all as read
    builder
      .addCase(markAllAsRead.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(markAllAsRead.fulfilled, (state, action) => {
        state.loading = false
        // Mark all notifications as read
        state.notifications = state.notifications.map(notification => ({
          ...notification,
          isRead: true,
        }))
        state.unreadCount = 0
        state.error = null
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Delete notification
    builder
      .addCase(deleteNotification.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.loading = false
        // Remove the notification from the list
        state.notifications = state.notifications.filter(notification => notification.id !== action.payload)
        // Update unread count
        state.unreadCount = state.notifications.filter(n => !n.isRead).length
        state.error = null
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Send direct notification
    builder
      .addCase(sendDirectNotification.pending, state => {
        state.sendLoading = true
        state.sendError = null
        state.sendSuccess = false
      })
      .addCase(sendDirectNotification.fulfilled, (state, action) => {
        state.sendLoading = false
        state.sendSuccess = true
        state.sendError = null

        // Optionally add the sent notification to the list if it's returned
        if (action.payload?.notification) {
          state.notifications.unshift(action.payload.notification)
        }
      })
      .addCase(sendDirectNotification.rejected, (state, action) => {
        state.sendLoading = false
        state.sendError = action.payload
        state.sendSuccess = false
      })

    // Send bulk notifications
    builder
      .addCase(sendBulkNotifications.pending, state => {
        state.sendLoading = true
        state.sendError = null
        state.sendSuccess = false
      })
      .addCase(sendBulkNotifications.fulfilled, (state, action) => {
        state.sendLoading = false
        state.sendSuccess = true
        state.sendError = null

        // Optionally add the sent notifications to the list if they're returned
        if (action.payload?.notifications && Array.isArray(action.payload.notifications)) {
          state.notifications = [...action.payload.notifications, ...state.notifications]
        }
      })
      .addCase(sendBulkNotifications.rejected, (state, action) => {
        state.sendLoading = false
        state.sendError = action.payload
        state.sendSuccess = false
      })

    // Send GDPR privacy policy notification
    builder
      .addCase(sendGdprPrivacyPolicyNotification.pending, state => {
        state.sendLoading = true
        state.sendError = null
        state.sendSuccess = false
      })
      .addCase(sendGdprPrivacyPolicyNotification.fulfilled, (state, action) => {
        state.sendLoading = false
        state.sendSuccess = true
        state.sendError = null

        // Optionally add the sent notification to the list if it's returned
        if (action.payload?.notification) {
          state.notifications.unshift(action.payload.notification)
        }
      })
      .addCase(sendGdprPrivacyPolicyNotification.rejected, (state, action) => {
        state.sendLoading = false
        state.sendError = action.payload
        state.sendSuccess = false
      })
  },
})

export const {
  readNotificationsRequest,
  readNotificationsSuccess,
  readNotificationsFail,
  clearSendStatus,
  clearError,
} = notificationsSlice.actions

export default notificationsSlice.reducer
